import { LitElement, html } from 'lit'
import { authService } from '../core/services/auth.service.js'
import { contaCacheService } from '../core/services/conta-cache.service.js'
import { responsavelCacheService } from '../core/services/responsavel-cache.service.js'
import { categoriaCacheService } from '../core/services/categoria-cache.service.js'
import { tagCacheService } from '../core/services/tag-cache.service.js'
import { orcamentoCacheService } from '../core/services/orcamento-cache.service.js'
import { transacaoCacheService } from '../core/services/transacao-cache.service.js'
import { iosDetectorService } from '../core/services/ios-detector.service.js'
import { AUTH_CONSTANTS } from '../core/constants/auth.constants.js'
import './install-prompt-ios.js'

/**
 * Componente Auth Guard — protege rotas autenticadas.
 *
 * Exibe um indicador de carregamento acessível enquanto o estado de autenticação
 * do Firebase não está resolvido, impedindo flash de conteúdo não autenticado.
 * Implementa timeout de segurança (10s) para evitar travamento indefinido.
 *
 * O conteúdo protegido (slot) NÃO é renderizado no DOM enquanto loading=true,
 * garantindo que nenhuma informação sensível apareça antes da autenticação.
 *
 * @element auth-guard
 * @slot - Conteúdo protegido, renderizado apenas quando autenticado
 */
export class AuthGuard extends LitElement {
  static properties = {
    isAuthenticated: { type: Boolean },
    loading: { type: Boolean },
    isDarkTheme: { type: Boolean },
    /** Indica se o timeout de autenticação expirou (10s) */
    authTimeout: { type: Boolean },
    /** Indica se o PWA pode ser instalado (beforeinstallprompt recebido) */
    _canInstallPWA: { type: Boolean, state: true },
    /** Indica se o botão de instalação iOS deve ser exibido */
    _canInstallPWA_iOS: { type: Boolean, state: true }
  }

  /**
   * Usa Light DOM para herdar estilos globais (Bulma, FontAwesome, tokens CSS).
   * Shadow DOM impediria que os estilos de navbar e ícones funcionassem corretamente.
   */
  createRenderRoot () {
    return this
  }

  constructor () {
    super()
    this.isAuthenticated = false
    this.loading = true
    this.isDarkTheme = this._getStoredTheme() === 'dark'
    this.authTimeout = false
    this.userEmail = 'rogerio.oliveira115@gmail.com'
    /** @type {number|null} Timer ID do timeout de autenticação */
    this._authTimeoutId = null
    /** @type {BeforeInstallPromptEvent|null} Evento PWA install capturado */
    this._deferredPrompt = null
    /** @type {boolean} Indica se o PWA pode ser instalado */
    this._canInstallPWA = false
    /** @type {boolean} Indica se o botão de instalação iOS deve ser exibido */
    this._canInstallPWA_iOS = false

    // Verifica iOS no construtor para disponibilizar no primeiro render (sem esperar connectedCallback)
    if (iosDetectorService.isIOS() && !iosDetectorService.isStandalone()) {
      this._canInstallPWA_iOS = !this._isIOSInstallDismissed()
    }

    this._applyInitialTheme()
  }

  /**
   * Lifecycle hook — componente adicionado ao DOM.
   * Inicia verificação de auth e timer de timeout de 10s.
   * Se o timeout expirar, redireciona para login como fallback de segurança.
   */
  connectedCallback () {
    super.connectedCallback()
    this._checkAuth()
    this._startAuthTimeout()
    this._updateChildrenVisibility()
    window.addEventListener('theme-changed', this._handleThemeChange.bind(this))
    window.addEventListener('auth-state-changed', this._handleAuthChange.bind(this))
    this._boundBeforeInstallPrompt = this._handleBeforeInstallPrompt.bind(this)
    this._boundAppInstalled = this._handleAppInstalled.bind(this)
    window.addEventListener('beforeinstallprompt', this._boundBeforeInstallPrompt)
    window.addEventListener('appinstalled', this._boundAppInstalled)

    // Fecha menu mobile ao clicar fora da navbar (comportamento similar a modais)
    this._boundOutsideClick = this._handleOutsideClick.bind(this)
    document.addEventListener('click', this._boundOutsideClick)

    // Verifica se o evento beforeinstallprompt já foi capturado antes do componente montar
    if (window.__deferredInstallPrompt) {
      this._deferredPrompt = window.__deferredInstallPrompt
      this._canInstallPWA = true
    }

    // Verifica se é iOS e não está em modo standalone para exibir botão de instalação iOS
    if (iosDetectorService.isIOS() && !iosDetectorService.isStandalone()) {
      this._canInstallPWA_iOS = !this._isIOSInstallDismissed()
    }
  }

  /**
   * Lifecycle hook — componente removido do DOM.
   * Limpa o timer de timeout para evitar memory leaks e efeitos indesejados.
   */
  disconnectedCallback () {
    super.disconnectedCallback()
    this._clearAuthTimeout()
    window.removeEventListener('theme-changed', this._handleThemeChange.bind(this))
    window.removeEventListener('auth-state-changed', this._handleAuthChange.bind(this))
    window.removeEventListener('beforeinstallprompt', this._boundBeforeInstallPrompt)
    window.removeEventListener('appinstalled', this._boundAppInstalled)
    document.removeEventListener('click', this._boundOutsideClick)
  }

  /**
   * Inicia timer de segurança de 10 segundos.
   * Se o estado de autenticação não resolver dentro desse prazo,
   * assume falha e redireciona para login como proteção.
   * @private
   */
  _startAuthTimeout () {
    this._authTimeoutId = setTimeout(() => {
      if (this.loading) {
        this.authTimeout = true
        this.loading = false
        window.location.hash = '#/login'
      }
    }, AUTH_CONSTANTS.AUTH_TIMEOUT)
  }

  /**
   * Limpa o timer de timeout de autenticação.
   * Chamado quando auth resolve (com ou sem user) ou no disconnectedCallback.
   * @private
   */
  _clearAuthTimeout () {
    if (this._authTimeoutId !== null) {
      clearTimeout(this._authTimeoutId)
      this._authTimeoutId = null
    }
  }

  /**
   * Controla a visibilidade dos children (páginas e nav-bar) do auth-guard.
   * Em Light DOM, os children existem no DOM real e precisam ser ocultados
   * manualmente durante loading ou quando não autenticado.
   * Seleciona apenas elementos filhos diretos que são custom elements (contém hífen no tagName).
   * @private
   */
  _updateChildrenVisibility () {
    const shouldShow = !this.loading && this.isAuthenticated
    for (const child of this.children) {
      // Custom elements (pages, nav-bar) têm hífen no tagName
      if (child.tagName && child.tagName.includes('-')) {
        child.style.display = shouldShow ? '' : 'none'
      }
    }
  }

  /**
   * Lifecycle hook — chamado após cada atualização de propriedades.
   * Atualiza visibilidade dos children quando loading ou isAuthenticated mudam.
   */
  updated (changedProperties) {
    super.updated(changedProperties)
    if (changedProperties.has('loading') || changedProperties.has('isAuthenticated')) {
      this._updateChildrenVisibility()
    }
  }

  /**
   * Obtém o tema armazenado no localStorage ou detecta via preferência do sistema.
   * Valores inválidos (diferentes de "dark" ou "light") são tratados como inexistentes,
   * aplicando fallback para prefers-color-scheme.
   * @returns {'dark' | 'light'} O tema a ser aplicado
   */
  _getStoredTheme () {
    try {
      const stored = localStorage.getItem('theme')
      if (stored === 'dark' || stored === 'light') {
        return stored
      }
    } catch (e) {
      // localStorage indisponível (modo privado ou bloqueado)
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }

  /**
   * Aplica o tema inicial ao carregar a aplicação.
   * Lê o tema do localStorage (ou detecta via prefers-color-scheme)
   * e aplica o atributo data-theme no elemento raiz do documento.
   */
  _applyInitialTheme () {
    const theme = this._getStoredTheme()
    document.documentElement.setAttribute('data-theme', theme)
    this.isDarkTheme = theme === 'dark'
  }

  _handleThemeChange (event) {
    this.isDarkTheme = event.detail.theme === 'dark'
  }

  /**
   * Verificação otimista do estado de autenticação.
   * Se authService já possui currentUser em cache (raro no reload), aceita imediatamente.
   * Caso contrário, mantém loading=true até o evento auth-state-changed ser disparado
   * pelo Firebase onAuthStateChanged — evitando redirecionamento prematuro para login no F5.
   */
  _checkAuth () {
    // Verificação otimista: se já autenticado (cache), aceita imediatamente
    if (authService.isAuthenticated()) {
      this.isAuthenticated = true
      this.loading = false
      this._clearAuthTimeout()
    }
    // Caso contrário, mantém loading = true até auth-state-changed
  }

  /**
   * Handler do evento auth-state-changed disparado pelo AuthService.
   * Quando auth resolve com usuário: limpa timeout e mostra conteúdo.
   * Quando auth resolve sem usuário: limpa timeout e redireciona para login
   * sem renderizar o conteúdo protegido em nenhum momento.
   * @param {CustomEvent} event - Evento com detail.isAuthenticated
   */
  _handleAuthChange (event) {
    this._clearAuthTimeout()
    this.isAuthenticated = event.detail.isAuthenticated
    this.loading = false

    // Redireciona imediatamente quando não autenticado, sem renderizar slot
    if (!this.isAuthenticated && window.location.hash !== '#/login') {
      window.location.hash = '#/login'
    }
  }

  async _handleLogout () {
    await authService.logout()
    window.location.hash = '#/login'
  }

  /**
   * Handler do evento beforeinstallprompt do navegador.
   * Captura o evento para uso posterior e exibe o botão de instalação.
   * O navegador dispara este evento quando o PWA atende aos critérios de instalação.
   * @param {BeforeInstallPromptEvent} event - Evento de instalação do PWA
   * @private
   */
  _handleBeforeInstallPrompt (event) {
    // Previne o mini-infobar padrão do Chrome
    event.preventDefault()
    // Armazena o evento para disparar o prompt quando o usuário clicar
    this._deferredPrompt = event
    this._canInstallPWA = true
    // Atualiza referência global para sincronizar com main.js
    window.__deferredInstallPrompt = event
  }

  /**
   * Handler do evento appinstalled — PWA foi instalado com sucesso.
   * Oculta o botão de instalação e limpa a referência ao prompt.
   * @private
   */
  _handleAppInstalled () {
    this._deferredPrompt = null
    this._canInstallPWA = false
    window.__deferredInstallPrompt = null
  }

  /**
   * Dispara o prompt de instalação do PWA quando o usuário clica no botão.
   * Usa o evento beforeinstallprompt capturado anteriormente.
   * Após a escolha do usuário, limpa a referência e oculta o botão.
   * @private
   */
  async _installPWA () {
    if (!this._deferredPrompt) return

    // Mostra o prompt nativo de instalação do navegador
    this._deferredPrompt.prompt()

    // Aguarda a escolha do usuário
    const { outcome } = await this._deferredPrompt.userChoice

    // Limpa a referência independente da escolha
    this._deferredPrompt = null
    window.__deferredInstallPrompt = null

    // Se instalou, oculta o botão
    if (outcome === 'accepted') {
      this._canInstallPWA = false
    }
  }

  /**
   * Abre o componente install-prompt-ios para exibir instruções de instalação no iOS.
   * Cria o componente no DOM se ainda não existir, ou reutiliza o existente.
   * Escuta o evento modal-closed para ocultar o botão após dismissão.
   * @private
   */
  _showIOSInstallPrompt () {
    let prompt = document.querySelector('install-prompt-ios')

    if (!prompt) {
      prompt = document.createElement('install-prompt-ios')
      document.body.appendChild(prompt)
    }

    // Escuta o fechamento do modal para atualizar estado do botão
    prompt.addEventListener('modal-closed', () => {
      this._canInstallPWA_iOS = !this._isIOSInstallDismissed()
    }, { once: true })

    prompt.open = true
  }

  /**
   * Verifica se o prompt de instalação iOS foi dismissido pelo usuário.
   * Consulta o localStorage com a chave de dismissão do componente install-prompt-ios.
   * Resiliente a localStorage indisponível (modo privado Safari) — retorna false como fallback.
   * @returns {boolean} true se o prompt foi dismissido, false caso contrário
   * @private
   */
  _isIOSInstallDismissed () {
    try {
      return localStorage.getItem('equilibra-ios-install-dismissed') === 'true'
    } catch {
      // localStorage indisponível (modo privado ou bloqueado)
      return false
    }
  }

  /**
   * Atualiza todos os caches da aplicação em paralelo.
   * Exibe indicador visual de loading durante a operação.
   */
  async _refreshCache () {
    const loadingShade = document.querySelector('loading-shade')

    try {
      // Mostrar loading imediatamente
      if (loadingShade) {
        loadingShade.show('Atualizando dados...')
      }

      // Aguardar um frame para garantir que o loading apareça
      await new Promise(resolve => requestAnimationFrame(resolve))

      // Forçar atualização dos caches em paralelo
      await Promise.all([
        contaCacheService.recarregar(),
        responsavelCacheService.recarregar(),
        categoriaCacheService.recarregar(),
        tagCacheService.recarregar(),
        orcamentoCacheService.recarregar(),
        transacaoCacheService.recarregar()
      ])

      // Tempo mínimo para mostrar o loading (UX)
      await new Promise(resolve => setTimeout(resolve, 1200))

      // Disparar eventos específicos
      window.dispatchEvent(new CustomEvent('contas-cache-updated'))
      window.dispatchEvent(new CustomEvent('responsaveis-cache-updated'))
      window.dispatchEvent(new CustomEvent('categorias-cache-updated'))
      window.dispatchEvent(new CustomEvent('tags-cache-updated'))
      window.dispatchEvent(new CustomEvent('orcamentos-cache-updated'))
      window.dispatchEvent(new CustomEvent('transacoes-cache-updated'))
    } catch (error) {
      console.error('❌ Erro ao atualizar cache de contas:', error)
    } finally {
      // Sempre esconder loading
      if (loadingShade) {
        loadingShade.hide()
      }
    }
  }

  /**
   * Alterna o tema entre dark e light de forma atômica.
   * Lê o tema atual do atributo data-theme no <html>, calcula o oposto,
   * e atualiza: (1) data-theme, (2) localStorage, (3) evento theme-changed.
   * As três operações ocorrem em sequência sem gaps assíncronos.
   */
  toggleTheme () {
    const currentTheme = document.documentElement.getAttribute('data-theme') || 'dark'
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark'

    // Operações atômicas em sequência
    document.documentElement.setAttribute('data-theme', newTheme)
    try {
      localStorage.setItem('theme', newTheme)
    } catch (e) {
      // localStorage indisponível (modo privado ou bloqueado)
    }
    window.dispatchEvent(new CustomEvent('theme-changed', {
      detail: { theme: newTheme }
    }))

    this.isDarkTheme = newTheme === 'dark'
  }

  /**
   * Alterna o estado do menu mobile (abrir/fechar) via burger button.
   * Atualiza aria-expanded para refletir o novo estado.
   * Resiliente a elementos ausentes no DOM (timing de renderização).
   * @private
   */
  _toggleMobileMenu () {
    const burger = this.renderRoot.querySelector('.navbar-burger')
    const menu = this.renderRoot.querySelector('.navbar-menu')

    if (burger) {
      burger.classList.toggle('is-active')
    }
    if (menu) {
      menu.classList.toggle('is-active')
    }

    // Atualiza aria-expanded baseado no estado atual
    const isExpanded = burger?.classList.contains('is-active') || false
    if (burger) {
      burger.setAttribute('aria-expanded', String(isExpanded))
    }
  }

  /**
   * Verifica se o viewport atual é mobile (≤ 1024px).
   * Usa window.innerWidth para determinar o breakpoint do Bulma.
   * Necessário para restringir o auto-close ao modo mobile,
   * mantendo o comportamento desktop inalterado.
   * @returns {boolean} true se viewport ≤ 1024px
   * @private
   */
  _isMobileViewport () {
    return window.innerWidth <= 1024
  }

  /**
   * Fecha o menu mobile ao clicar fora da área da navbar.
   * Verifica se o menu está aberto e se o clique ocorreu fora do elemento nav.
   * Só atua em viewport mobile (≤ 1024px).
   * @param {MouseEvent} event - Evento de clique no document
   * @private
   */
  _handleOutsideClick (event) {
    if (!this._isMobileViewport()) return

    const menu = this.renderRoot.querySelector('.navbar-menu')
    if (!menu || !menu.classList.contains('is-active')) return

    // Verifica se o clique foi fora da navbar
    const navbar = this.renderRoot.querySelector('.navbar')
    if (navbar && !navbar.contains(event.target)) {
      this._closeMenuMobile()
    }
  }

  /**
   * Fecha o menu mobile removendo a classe is-active do burger e do menu.
   * Atualiza o atributo aria-expanded para refletir o estado fechado.
   * Método seguro — não lança exceção se elementos não existirem no DOM.
   * Centraliza a lógica de fechamento para ser reutilizado por event delegation.
   * @private
   */
  _closeMenuMobile () {
    const burger = this.renderRoot.querySelector('.navbar-burger')
    const menu = this.renderRoot.querySelector('.navbar-menu')

    if (menu) {
      menu.classList.remove('is-active')
    }
    if (burger) {
      burger.classList.remove('is-active')
      burger.setAttribute('aria-expanded', 'false')
    }
  }

  /**
   * Handler delegado para cliques em itens de ação dentro do navbar-menu.
   * Verifica se o clique ocorreu em um a.navbar-item de ação e se estamos em
   * viewport mobile (≤ 1024px). Se sim, fecha o menu após a ação executar.
   * Usa event delegation no navbar-menu para capturar todos os itens.
   *
   * Condições verificadas:
   * 1. Viewport mobile (≤ 1024px) — ignora em desktop
   * 2. Clique em a.navbar-item — ignora cliques em áreas vazias
   * 3. Não é o próprio burger — ele tem seu próprio handler de toggle
   *
   * @param {Event} event - Evento de clique propagado via delegation
   * @private
   */
  _handleMenuItemClick (event) {
    // Verifica se estamos em viewport mobile
    if (!this._isMobileViewport()) return

    // Verifica se o clique foi em um item de ação (anchor com ação)
    const actionItem = event.target.closest('a.navbar-item')
    if (!actionItem) return

    // Ignora o próprio burger (ele tem seu próprio handler)
    if (actionItem.classList.contains('navbar-burger')) return

    // Fecha o menu mobile após a ação ser processada
    this._closeMenuMobile()
  }

  /**
   * Renderiza o componente com base no estado de autenticação.
   * Em Light DOM, os children (páginas) já existem no DOM real.
   * O controle de visibilidade é feito via estilos e overlay de loading.
   * - loading=true: overlay de loading sobre tudo, children ocultos
   * - autenticado: navbar visível + children visíveis
   * - não autenticado: redireciona para login
   */
  render () {
    // Enquanto loading, exibe indicador acessível e oculta children
    if (this.loading) {
      return html`
        <div
          class="auth-guard-loading"
          role="status"
          aria-label="Verificando autenticação, por favor aguarde"
          data-cy="auth-guard-loading"
          style="display:flex;flex-direction:column;align-items:center;justify-content:center;width:100vw;height:100vh;background-color:var(--bg-primary, #0F162D);position:fixed;top:0;left:0;z-index:9999;"
        >
          <div class="spinner" style="width:48px;height:48px;border:4px solid var(--text-secondary, #A8B3CF);border-top-color:var(--primary, #6120D8);border-radius:50%;animation:spin 0.8s linear infinite;"></div>
          <p class="loading-text" style="margin-top:16px;color:var(--text-secondary, #A8B3CF);font-size:1rem;">Verificando autenticação...</p>
          <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
        </div>
      `
    }

    // Não autenticado: não renderiza conteúdo
    if (!this.isAuthenticated) {
      return html``
    }

    // Autenticado: renderiza navbar superior (children renderizam-se via Light DOM)
    return html`
      <style>
        /* ===================================================================
         * CSS da Navbar — Rótulos de texto mobile (Spec 023)
         * Rótulos ficam ocultos em desktop e visíveis quando menu mobile aberto.
         * Implementa truncamento, gap, alinhamento e altura mínima WCAG 2.5.5.
         * =================================================================== */

        /* Rótulos ocultos por padrão em desktop (> 1024px) */
        .navbar-item-label {
          display: none;
        }

        /* ===================================================================
         * Elementos mobile ocultos por padrão (desktop > 1024px)
         * Email informativo, divisor visual e botão logout flat são exclusivos
         * do menu mobile — ficam ocultos em desktop via display: none.
         * A media query mobile (task 2.2+) sobrescreve para exibi-los.
         * =================================================================== */
        .mobile-user-email,
        .mobile-logout-divider,
        .mobile-btn-logout {
          display: none;
        }

        /* Regras para viewport mobile (≤ 1024px) com menu aberto */
        @media (max-width: 1024px) {
          /* Rótulos visíveis quando menu hamburger está aberto */
          .navbar-menu.is-active .navbar-item-label {
            display: inline;
            /* Truncamento de texto longo (máx 30 caracteres visíveis) */
            max-width: 30ch;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          /* Layout flex com gap entre ícone e texto nos itens de ação */
          .navbar-menu.is-active a.navbar-item {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          /* Altura mínima de 44px nos itens quando menu aberto (WCAG 2.5.5) */
          .navbar-menu.is-active .navbar-item {
            min-height: 44px;
            display: flex;
            align-items: center;
          }
        }

        /* ===================================================================
         * CSS do Menu Mobile — Layout Flat Logout (Spec 024)
         * Media query para exibir elementos mobile e ocultar dropdown desktop.
         * Todos os estilos usam tokens CSS com fallback dark mode.
         * =================================================================== */
        @media (max-width: 1024px) {
          /* Oculta dropdown desktop no mobile (Req 4.1, 4.3, 4.4) */
          .navbar-item.has-dropdown.is-hoverable {
            display: none !important;
          }

          /* Email informativo visível no mobile (Req 1.1-1.4, 5.1, 5.4) */
          .navbar-menu.is-active .mobile-user-email {
            display: flex;
            align-items: center;
            font-size: 0.85rem;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            cursor: default;
            pointer-events: none;
          }

          /* Divisor visual acima do logout (Req 3.1-3.3, 5.4) */
          .navbar-menu.is-active .mobile-logout-divider {
            display: block !important;
            border-top: 1px solid var(--border, #26304A);
            margin-top: 8px;
            padding: 0;
            height: 0;
            min-height: 0 !important;
          }

          /* Botão Sair flat no mobile (Req 2.2, 2.3, 5.2-5.4) */
          .navbar-menu.is-active .mobile-btn-logout {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            min-height: 48px;
            font-size: 1rem;
            font-weight: 400;
            transition: background-color 200ms ease;
          }

          /* Estados hover/active do botão Sair (Req 5.3) */
          .navbar-menu.is-active .mobile-btn-logout:hover,
          .navbar-menu.is-active .mobile-btn-logout:active {
            background-color: var(--bg-surface-secondary, #1D2740);
          }

          /* Alinhamento à esquerda dos itens de ação no menu mobile */
          .navbar-menu.is-active .navbar-end {
            text-align: left;
          }

          .navbar-menu.is-active .navbar-end .navbar-item {
            justify-content: flex-start;
          }

          .navbar-menu.is-active .navbar-end a.navbar-item {
            justify-content: flex-start;
            width: 100%;
          }
        }
      </style>
      <div class="app-container" data-cy="auth-guard-content">
        <nav class="navbar is-fixed-top" role="navigation" data-cy="top-navbar">
          <div class="navbar-brand">
            <a class="navbar-item navbar-item--static" href="#/dashboard" data-cy="navbar-brand">
              <img src="/assets/images/logo/logo-icon-trim.png" alt="EquiLibra" height="52" style="margin-right: 8px;">
              <img src="/assets/images/logo/logo-text-trim.png" alt="EquiLibra" height="48">
            </a>

            ${this._canInstallPWA_iOS ? html`
            <a class="navbar-item"
               role="button"
               aria-label="Instalar aplicativo"
               data-cy="btn-install-pwa-ios"
               @click="${this._showIOSInstallPrompt}">
              <i class="fas fa-download"></i>
            </a>
            ` : ''}

            <a role="button" class="navbar-burger" aria-label="menu" aria-expanded="false" data-target="navbarMenu" data-cy="navbar-burger" @click="${this._toggleMobileMenu}">
              <span aria-hidden="true"></span>
              <span aria-hidden="true"></span>
              <span aria-hidden="true"></span>
            </a>
          </div>

          <div id="navbarMenu" class="navbar-menu" data-cy="navbar-menu"
               @click="${this._handleMenuItemClick}">
            <div class="navbar-end">
              <!-- Email informativo do usuário logado (visível apenas no mobile) -->
              <div class="navbar-item mobile-user-email"
                   data-cy="mobile-user-email"
                   aria-label="Conta logada"
                   role="status">
                ${authService.getCurrentUser()?.email || 'Usuário'}
              </div>
              ${this._canInstallPWA && !this._canInstallPWA_iOS ? html`
              <a class="navbar-item mobile-install-pwa"
                 role="button"
                 aria-label="Instalar aplicativo"
                 data-cy="btn-install-pwa"
                 @click="${this._installPWA}">
                <i class="fas fa-download"></i>
                <span class="navbar-item-label" data-cy="label-install-pwa">Instalar</span>
              </a>
              ` : ''}
              <a class="navbar-item" role="button" @click="${this._refreshCache}" data-cy="btn-refresh-cache" title="Atualizar dados">
                <i class="fas fa-sync-alt"></i>
                <span class="navbar-item-label" data-cy="label-refresh-cache">Atualizar dados</span>
              </a>
              <a class="navbar-item" role="button" @click="${this.toggleTheme}" data-cy="btn-theme-toggle" title="Alternar tema">
                <i class="fas ${this.isDarkTheme ? 'fa-sun' : 'fa-moon'}"></i>
                <span class="navbar-item-label" data-cy="label-theme-toggle">${this.isDarkTheme ? 'Tema claro' : 'Tema escuro'}</span>
              </a>
              <div class="navbar-item has-dropdown is-hoverable" data-cy="desktop-dropdown-user">
                <a class="navbar-link" data-cy="user-menu">
                  ${authService.getCurrentUser()?.email || 'Usuário'}
                </a>
                <div class="navbar-dropdown">
                  <a class="navbar-item" @click="${this._handleLogout}" data-cy="btn-logout">
                    <i class="fas fa-sign-out-alt" style="margin-right: 0.5rem;"></i> Sair
                  </a>
                </div>
              </div>
              <!-- Divisor visual separando logout dos demais itens (visível apenas no mobile) -->
              <div class="navbar-item mobile-logout-divider"
                   data-cy="mobile-logout-divider">
              </div>
              <!-- Botão Sair flat (visível apenas no mobile) -->
              <a class="navbar-item mobile-btn-logout"
                 role="button"
                 aria-label="Sair"
                 data-cy="mobile-btn-logout"
                 @click="${this._handleLogout}">
                <i class="fas fa-sign-out-alt"></i>
                <span>Sair</span>
              </a>
            </div>
          </div>
        </nav>
      </div>
    `
  }
}

customElements.define('auth-guard', AuthGuard)
